// frontend/src/services/shortcutSegmentService.js
// Service for managing shortcut segment references from reordered trips
// See docs/ETA_TRIP_TRACKING_REORDERABLE_WAYPOINTS.md

import axios from '../axiosConfig';
import { buildSegmentKey } from '../utils/segmentUtils';

const shortcutSegmentService = {
    /**
     * Detect shortcut edges for a completed trip.
     * A shortcut edge is a non-sequential waypoint transition (e.g., A→C skipping B).
     *
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID (must be completed)
     * @returns {Promise<Object>} {
     *   success: boolean,
     *   hasReorderedWaypoints: boolean,
     *   shortcutEdges: Array<{ fromWaypointId, toWaypointId, fromName, toName }>,
     *   tripArchivedType: string,
     *   routeId: number
     * }
     */
    detectShortcutEdges: async (entityId, tripId) => {
        const response = await axios.get(
            `/api/trips/${entityId}/trips/${tripId}/shortcut-edges`
        );
        return response.data;
    },

    /**
     * Get existing shortcuts for multiple edges (batch).
     * Used to determine default checkbox states in the Save Shortcut Segments dialog.
     *
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {Array} edges - Array of { fromWaypointId, toWaypointId }
     * @returns {Promise<Object>} {
     *   success: boolean,
     *   existingShortcuts: Object<string, Array<{ archived_type, point_count, recorded_at }>>
     * }
     */
    getExistingShortcuts: async (entityId, routeId, edges) => {
        const response = await axios.post(
            `/api/trips/${entityId}/routes/${routeId}/existing-shortcuts`,
            { edges }
        );
        return response.data;
    },

    /**
     * Get segment-level reference polylines (shortcuts + variants) for a route.
     * Backend resolves archived_type as representative-first when omitted.
     * Also returns available types for populating the type selector dropdown.
     *
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {string|null} archivedType - Optional archived_type filter
     * @returns {Promise<Object>} {
     *   success: boolean,
     *   segments: Array,
     *   segmentCount: number,
     *   archivedType: string,
     *   availableTypes: Array<{ typeKey: string, displayName: string }>
     * }
     */
    getSegmentPolylines: async (entityId, routeId, archivedType = null) => {
        const params = {};
        if (archivedType) {
            params.archived_type = archivedType;
        }

        const response = await axios.get(
            `/api/trips/${entityId}/routes/${routeId}/segment-polylines`,
            { params }
        );
        return response.data;
    },

    /**
     * Save shortcut segments from a completed trip to selected archived_types.
     *
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID (must be completed)
     * @param {Array<Object>} targets - Array of { fromWaypointId, toWaypointId, archivedType, variant }
     * @returns {Promise<Object>} {
     *   success: boolean,
     *   saved: Array - Successfully saved edges,
     *   skipped: Array - Edges that couldn't be saved (insufficient data),
     *   errors: Array - Any errors encountered
     * }
     */
    saveShortcuts: async (entityId, tripId, targets = []) => {
        const groupsByType = new Map();
        for (const target of targets) {
            const archivedType = target.archivedType || 'representative';
            if (!groupsByType.has(archivedType)) {
                groupsByType.set(archivedType, []);
            }
            groupsByType.get(archivedType).push({
                fromWaypointId: target.fromWaypointId,
                toWaypointId: target.toWaypointId,
                variant: target.variant ?? null
            });
        }

        const referenceGroups = Array.from(groupsByType.entries()).map(([referenceType, segments]) => ({
            referenceType,
            segments
        }));

        const response = await axios.post(
            `/api/trips/${entityId}/trips/${tripId}/save-reference-data`,
            {
                workflow: 'detected_shortcut',
                referenceGroups
            }
        );
        return response.data;
    },

    /**
     * Helper: Determine default checkbox states for each edge based on existing shortcuts.
     * Implements the decision tree from the plan:
     * - Representative: CHECKED if no existing shortcut, UNCHECKED if exists
     * - Trip's archived_type: Always UNCHECKED (optional)
     *
     * @param {Array} shortcutEdges - Array of detected shortcut edges
     * @param {Object} existingShortcuts - Map of "fromId-toId" → Array of { archived_type, ... }
     * @param {string} tripArchivedType - The trip's selected archived_type
     * @returns {Object} Map of edge keys to default checkbox states
     */
    getDefaultCheckboxStates: (shortcutEdges, existingShortcuts, tripArchivedType) => {
        const defaultStates = {};

        for (const edge of shortcutEdges) {
            const edgeKey = buildSegmentKey(edge.fromWaypointId, edge.toWaypointId);
            const existingForEdge = existingShortcuts[edgeKey] || [];

            // Check if representative shortcut exists for this edge
            const hasRepresentative = existingForEdge.some(s => s.archived_type === 'representative');

            // Check if trip's type shortcut exists for this edge
            const hasTripType = existingForEdge.some(s => s.archived_type === tripArchivedType);

            defaultStates[edgeKey] = {
                // Representative: checked by default if no existing, unchecked if exists
                representative: !hasRepresentative,
                representativeExists: hasRepresentative,
                representativeRecordedAt: existingForEdge.find(s => s.archived_type === 'representative')?.recorded_at,

                // Trip's type: always unchecked by default (optional)
                [tripArchivedType]: false,
                tripTypeExists: hasTripType,
                tripTypeRecordedAt: existingForEdge.find(s => s.archived_type === tripArchivedType)?.recorded_at
            };
        }

        return defaultStates;
    }
};

export default shortcutSegmentService;
